import { HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useKey } from 'react-use'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Burger from '@/components/base/burger'
import Button from '@/components/base/button'
import SVGIcon from '@/components/base/svg-icon'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import LanguageList from '@/components/layout/language-list'
import Menu from '@/components/layout/menu'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { useRouterStore } from '@/store/router'
import { pageHasHero } from '@/utils/page'
import { stripSlash } from '@/utils/path'
import { isAcademy, isCorporate } from '@/constants'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type HeaderProps = HTMLAttributes<HTMLDivElement> & {
  pageProps: WPPageData
}

const Header = ({ className, pageProps, ...props }: HeaderProps) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const { strings, menu } = useStore()
  const router = useRouter()
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)
  const currentLocale = useLocaleStore((state) => state.currentLocale)

  const isActive = useCallback(
    (url: string) =>
      stripSlash(url) ===
      stripSlash(
        `${currentLocale !== router.defaultLocale ? `/${currentLocale}` : ''}${router.asPath}`
      ),
    [router, currentLocale]
  )

  useKey('Escape', () => setLayoutState({ menuOpen: false }))

  useEffect(
    () => setLayoutState({ lockScroll: menuOpen }),
    [menuOpen, setLayoutState]
  )

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => setLayoutState({ menuOpen: false })
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <>
      <header
        className={cn(
          'fixed left-0 right-0 top-0 z-header',
          'flex h-header-height items-center',
          'transition-all duration-300 ease-in-out-cubic',
          'bg-section-backgroud-light md:bg-transparent dark:bg-section-backgroud-dark dark:md:bg-transparent',
          '[.header-contained_&]:h-header-height-contained [.header-contained_&]:bg-section-backgroud-light dark:[.header-contained_&]:bg-section-backgroud-dark',
          {
            'md:bg-section-backgroud-light': isAcademy
          },
          className
        )}
        {...props}
      >
        <div className="flex w-full items-center justify-between px-margin xl:px-[8rem]">
          <div className="flex">
            <Link
              href="/"
              scroll={false}
              className={cn(
                'flex max-h-[9.1rem] rounded-radius-minimal',
                'border border-transparent [.header-contained_&]:border-transparent',
                'px-spacing-2xs py-[0.9rem] md:px-spacing-sm md:py-spacing-xs',
                'transition-colors duration-500 ease-in-out-cubic',
                {
                  'bg-section-backgroud-light md:border-border-menu-light/30 dark:bg-section-backgroud-dark dark:md:border-border-menu-dark/30':
                    !menuOpen && hasHero && isCorporate
                }
              )}
            >
              <span className="sr-only">{strings?.brand_name}</span>
              {isCorporate ? (
                <>
                  <SVGIcon
                    symbol="logo-corporate"
                    className="hidden fill-surface-logo-light lg:block lg:h-[5.9rem] lg:w-[12rem] dark:fill-surface-logo-dark"
                  />
                  <SVGIcon
                    symbol="logo-corporate-condensed"
                    className="h-[2.8rem] w-[8.5rem] fill-surface-logo-light lg:hidden dark:fill-surface-logo-dark"
                  />
                </>
              ) : (
                <>
                  <SVGIcon
                    symbol="logo-academy"
                    className="hidden lg:h-[5.4rem] lg:w-[34.8rem] xl:block"
                  />
                  <SVGIcon
                    symbol="logo-academy-condensed"
                    className="h-[4rem] w-[14rem] xl:hidden"
                  />
                </>
              )}
            </Link>
          </div>

          <div className="flex items-center space-x-[.4rem] md:space-x-spacing-xs">
            <nav className="hidden p-spacing-sm lg:flex">
              <ul className="flex items-center space-x-spacing-xs">
                {menu?.header_menu_01?.menu?.map(
                  ({ url, title, target }, index) => (
                    <li key={`header-menu-01-${index}`} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className={cn([
                          'inline-flex items-center',
                          '-m-[0.5rem] whitespace-nowrap p-[0.5rem]',
                          'transition-colors duration-500 ease-in-out-cubic',
                          'text-text-menu-background-light dark:text-text-menu-background-dark',
                          {
                            'text-text-menu-light dark:text-text-menu-dark':
                              hasHero && isCorporate
                          },
                          '[.header-contained_&]:text-text-menu-background-light dark:[.header-contained_&]:text-text-menu-background-dark',
                          {
                            'text-text-accent-light':
                              isActive(url) && (!hasHero || isAcademy),
                            '[.header-contained_&]:text-text-accent-light':
                              isActive(url)
                          },
                          '[.page-transition_&]:text-text-menu-background-light [.page-transition_&]:ease-out-cubic dark:[.page-transition_&]:text-text-menu-background-dark',

                          { 'pointer-events-none': isActive(url) }
                        ])}
                      >
                        <span
                          className={cn(
                            'grid',
                            'transition-all duration-500 ease-out-cubic',
                            {
                              'grid-cols-[1fr]': isActive(url),
                              'grid-cols-[0fr]': !isActive(url)
                            },
                            '[.page-transition_&]:grid-cols-[0fr]'
                          )}
                        >
                          <span
                            className={cn(
                              'overflow-hidden',
                              'before:mr-spacing-xs before:mt-[1px] before:block before:size-[0.4rem] before:rounded-full before:bg-current'
                            )}
                          />
                        </span>
                        <Typography
                          variant="menu"
                          className={cn(
                            'text-current',
                            'hover:opacity-60',
                            'underline-effect transition-opacity duration-300 ease-out-cubic',
                            {
                              'underline-active opacity-80': isActive(url)
                            },
                            '[.page-transition_&]:opacity-100'
                          )}
                        >
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </nav>

            <nav
              className={cn([
                'transition-opacity duration-300 ease-out-cubic',
                { 'opacity-0 lg:opacity-100': menuOpen }
              ])}
            >
              <ul className="flex items-center space-x-spacing-xs">
                {menu?.header_menu_02?.menu?.map(
                  ({ url, title, target }, index) => (
                    <li key={`header-menu-02-${index}`}>
                      <Button asChild variant="switch" isLiquid>
                        <Link href={url} target={target} scroll={false}>
                          <TextEffect>
                            <Typography variant="regular">{title}</Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    </li>
                  )
                )}
              </ul>
            </nav>

            <LanguageList
              className="hidden lg:flex"
              itemClassName={cn(
                'transition-colors duration-500 ease-in-out-cubic',
                'text-text-menu-background-light dark:text-text-menu-background-dark',
                {
                  'text-text-menu-light dark:text-text-menu-dark':
                    hasHero && isCorporate
                },
                '[.header-contained_&]:text-text-menu-background-light dark:[.header-contained_&]:text-text-menu-background-dark',
                '[.page-transition_&]:text-text-menu-background-light dark:[.page-transition_&]:text-text-menu-background-dark'
              )}
            />

            <Burger
              onClick={() => setLayoutState({ menuOpen: !menuOpen })}
              className="lg:hidden"
              open={menuOpen}
            />
          </div>
        </div>
      </header>
      <Transition
        nodeRef={menuRef}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 600
        }}
        unmountOnExit
      >
        {(state) => <Menu ref={menuRef} state={state} />}
      </Transition>
    </>
  )
}

export default Header
